import { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SecurityIcon from '@mui/icons-material/Security';
import ComputerIcon from '@mui/icons-material/Computer';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import HistoryIcon from '@mui/icons-material/History';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import CommuteIcon from '@mui/icons-material/Commute';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from '../../api/axiosInstance';
import { useAuth } from '../../context/AuthContext';
import { PieChart } from '@mui/x-charts/PieChart';
import { DataGrid } from '@mui/x-data-grid';

const TYPES = [
  { value: 'technique', label: 'Technique' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'administratif', label: 'Administratif' },
];

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatMonthRange(value) {
  if (!value) return 'Période mensuelle non définie';
  const start = new Date(value);
  if (Number.isNaN(start.getTime())) return 'Période mensuelle non définie';
  const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
  return `${formatDate(start)} au ${formatDate(end)}`;
}


function formatHours(value) {
  const number = Number(value || 0);
  return `${number.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} h`;
}

function progressValue(used, total) {
  const numericUsed = Number(used || 0);
  const numericTotal = Number(total || 0);
  if (!numericTotal) return numericUsed > 0 ? 100 : 0;
  return Math.min(100, Math.round((numericUsed / numericTotal) * 100));
}

function MetricCard({ icon, label, value, helper, color = 'primary.main' }) {
  return (
    <Card variant="elevation" sx={{ height: '100%', border: 0, borderRadius: 3, backgroundImage: 'linear-gradient(145deg, rgba(8,127,112,.12), rgba(255,255,255,.98) 75%)', boxShadow: '0 6px 20px rgba(20,40,60,.08)' }}>
      <CardContent sx={{ minHeight: 174, display: 'flex', alignItems: 'center' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ color, width: 42, height: 42, borderRadius: 2, bgcolor: 'rgba(8,127,112,.12)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</Box>
          <Box>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>{value}</Typography>
            {helper && <Typography variant="caption" color="text.secondary">{helper}</Typography>}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function UsageCard({ title, used, total, footer, unit = 'hours' }) {
  const visual = title.includes('admin') ? { icon: <AdminPanelSettingsIcon />, color: '#087f70' } : title.includes('ingé') ? { icon: <EngineeringIcon />, color: '#2476b9' } : title.includes('pilotage') ? { icon: <ManageAccountsIcon />, color: '#7652b8' } : { icon: <CommuteIcon />, color: '#d17b24' };
  const formatMetric = (value) => unit === 'count'
    ? Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })
    : formatHours(value);
  const numericUsed = Number(used || 0);
  const numericTotal = Number(total || 0);
  const percent = progressValue(numericUsed, numericTotal);
  const overrun = numericUsed > numericTotal;
  const overrunHours = Math.max(0, numericUsed - numericTotal);
  const overrunPercent = numericTotal > 0 ? Math.round((overrunHours / numericTotal) * 100) : (numericUsed > 0 ? 100 : 0);
  const remaining = Math.max(0, numericTotal - numericUsed);
  const displayPercent = overrun ? overrunPercent : percent;

  return (
    <Paper
      variant="elevation"
      sx={{
        p: 2.25,
        borderRadius: 3,
        textAlign: 'center',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 335,
        height: '100%',
        border: 'none',
        bgcolor: overrun ? 'rgba(211, 47, 47, 0.06)' : 'background.paper',
        backgroundImage: overrun
          ? 'linear-gradient(145deg, rgba(211,47,47,.14), rgba(255,255,255,.96) 72%)'
          : `linear-gradient(145deg, ${visual.color}18, rgba(255,255,255,.98) 72%)`,
      }}
    >
      <Stack spacing={1.25} sx={{justifyContent: 'center', alignItems: 'center'}}>
        <Stack direction="row" justifyContent="center" alignItems="center">
          <Stack direction="row" spacing={1}  sx={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ width: 34, height: 34, borderRadius: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: visual.color, bgcolor: `${visual.color}18` }}>
              {visual.icon}
            </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {title}
            </Typography>
            </Stack>
        </Stack>
        <Box sx={{ position: 'relative', width: 165, height: 165, mx: 'auto' }}>
          <PieChart
            width={165}
            height={165}
            hideLegend
            series={[{ data: [{ id: 'used', value: percent, color: overrun ? '#d32f2f' : visual.color }, { id: 'remaining', value: Math.max(0, 100 - percent), color: overrun ? '#f2b8b5' : '#d7e8dc' }], innerRadius: 53, outerRadius: 73, paddingAngle: 1, cornerRadius: 4 } ]}
            margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
          />
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
            <Typography variant="caption" sx={{ fontWeight: 900, color: overrun ? 'error.main' : 'success.dark' }}>{overrun ? '+' : ''}{displayPercent}%</Typography>
            <Typography variant="caption" sx={{ fontSize: '0.68rem', lineHeight: 1.1, fontWeight: 700 }}>{overrun ? `${formatMetric(overrunHours)} dépassé` : `${formatMetric(remaining)} disponible`}</Typography>
          </Box>
        </Box>
        <Typography variant="body2" color={overrun ? 'error.main' : 'text.secondary'} sx={{ textAlign: 'center', fontWeight: overrun ? 700 : 400 }}>
          {formatMetric(numericUsed)} utilisé / {formatMetric(numericTotal)} prévu
        </Typography>
      </Stack>
    </Paper>
  );
}

function SectionCard({ title, action, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2, height: '100%', }} elevation={4}>
      <Stack spacing={1.75}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
          {action}
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}


function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase() || '?';
}

function PortfolioContact({ label, user }) {
  if (!user?.name) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: 'success.main',
          color: 'common.white',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.68rem',
          fontWeight: 800,
          flexShrink: 0,
        }}
      >
        {getInitials(user.name)}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>{user.name}</Typography>
        {user.email ? <Typography variant="caption" color="text.secondary" noWrap>{user.email}</Typography> : null}
      </Box>
    </Stack>
  );
}

export default function ClientDashboardHome() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deviceTotal, setDeviceTotal] = useState(null);
  const [usagePeriod, setUsagePeriod] = useState('month');

  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const [response, devicesResponse, agentsResponse] = await Promise.all([
        axios.get('/api/client/dashboard'),
        axios.get('/api/eset/devices').catch(() => ({ data: { devices: [] } })),
        axios.get('/api/agents').catch(() => ({ data: { data: [] } })),
      ]);
      setData(response.data);
      const clientOrg = response.data?.organization?.name;
      const belongsToClient = (item) => {
        const org = item.organizationName || item.organization?.name || item.organizationDetails?.name || item.Organization?.name;
        return !clientOrg || !org || String(org).trim().toLowerCase() === String(clientOrg).trim().toLowerCase();
      };
      const eset = (devicesResponse.data?.devices || devicesResponse.data?.data || []).filter(belongsToClient);
      const agents = (agentsResponse.data?.data || agentsResponse.data?.agents || []).filter(belongsToClient);
      const keys = new Set();
      [...eset.filter((item) => !item.deleted), ...agents.filter((item) => !item.deleted)].forEach((item) => {
        const inventory = item.lastInventory || {};
        const serial = item.serialNumber || item.serial_number || item.hardware?.[0]?.serialNumber
          || inventory.hardware?.serialNumber || inventory.hardware?.serial || inventory.raw?.hardware?.serialNumber;
        const hostname = item.hostname || item.name || item.agentId;
        const key = serial || hostname;
        if (key) keys.add(String(key).trim().toUpperCase().replace(/[^A-Z0-9]/g, ''));
      });
      setDeviceTotal(keys.size || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Impossible de charger votre tableau de bord.');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadDashboard();
  }, []);

  const contractPeriodLabel = data?.period?.contractPeriodStart && data?.period?.contractPeriodEnd
    ? `${formatDate(data.period.contractPeriodStart)} au ${formatDate(data.period.contractPeriodEnd)}`
    : 'Période contractuelle non définie';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const usageKey = usagePeriod === 'year' ? 'year' : 'month';
  const regularizationUsage = data.regularization?.[usageKey];
  const fallbackUsage = usagePeriod === 'year' ? data.time?.year || {} : data.time?.month || {};
  const usageData = regularizationUsage?.used || fallbackUsage;
  const usageIncluded = regularizationUsage?.included || {};
  const plannedAdmin = usageIncluded.admin ?? (data.contracts?.monthlyIncluded?.admin || 0) * (usagePeriod === 'year' ? 12 : 1);
  const plannedEngineer = usageIncluded.engineer ?? (data.contracts?.monthlyIncluded?.engineer || 0) * (usagePeriod === 'year' ? 12 : 1);
  const plannedPilotage = usageIncluded.pilotage ?? (data.contracts?.monthlyIncluded?.pilotage || 0) * (usagePeriod === 'year' ? 12 : 1);
  const plannedTravel = usageIncluded.travel ?? 0;
  const agentboxDevices = data.services?.agentbox?.devices || data.services?.agentBox?.devices || data.services?.agents?.devices || 0;
  const supervisedDevices = Number(data.services?.eset?.devices || 0) + Number(data.services?.level?.devices || 0) + Number(agentboxDevices);
  const usageRangeLabel = usagePeriod === 'year' ? contractPeriodLabel : formatMonthRange(data.period?.monthStart);
  const portfolioAccounts = [
    ...(data.portfolio?.accounts || []).slice().sort((a, b) => Number(a.priority || 0) - Number(b.priority || 0)),
    ...(data.portfolio?.items || []).flatMap((item) => (
      (item.accounts || []).slice().sort((a, b) => Number(a.priority || 0) - Number(b.priority || 0))
    )),
  ].filter((account) => account?.user?.name).reduce((accounts, account) => {
    const key = account.user._id || account.user.email || account.user.name;
    if (!accounts.some((item) => (item.user._id || item.user.email || item.user.name) === key)) accounts.push(account);
    return accounts;
  }, []);
  const primaryContact = portfolioAccounts[0]?.user;
  const secondaryContact = portfolioAccounts[1]?.user;
  const additionalContacts = portfolioAccounts.slice(2).map((account) => account.user);
  const recentTicketRows = (() => {
    const tickets = data.tickets?.recent || [];
    const timeEntries = data.time?.recent || [];
    const entriesByTicket = new Map();
    timeEntries.forEach((entry) => {
      const key = String(entry.ticketId || '');
      if (!key) return;
      if (!entriesByTicket.has(key)) entriesByTicket.set(key, []);
      entriesByTicket.get(key).push(entry);
    });

    const rows = tickets.map((ticket) => ({
      ticket,
      entries: entriesByTicket.get(String(ticket.ticketId || '')) || [],
    }));
    const knownTicketIds = new Set(rows.map(({ ticket }) => String(ticket.ticketId || '')));
    timeEntries.forEach((entry) => {
      const key = String(entry.ticketId || '');
      if (!key || knownTicketIds.has(key)) return;
      rows.push({
        ticket: {
          _id: `time-${entry._id}`,
          ticketId: entry.ticketId,
          subject: entry.ticketSubject || entry.activity || 'Intervention',
          assignedTo: entry.user,
          updatedAt: entry.spentOn,
          status: entry.ticketStatus || 'Temps passé',
        },
        entries: [entry],
      });
      knownTicketIds.add(key);
    });
    return rows;
  })();

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', py: 1 }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="overline" sx={{ color: 'success.main', fontWeight: 800 }}>Espace client</Typography>
            <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
              {data.organization?.name || 'Tableau de bord'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Bonjour {user?.firstname || ''}, synthèse des services, demandes et temps consommé.
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Période</InputLabel>
            <Select label="Période" value={usagePeriod} onChange={(event) => setUsagePeriod(event.target.value)}>
              <MenuItem value="month">Mois en cours</MenuItem>
              <MenuItem value="year">Période contractuelle en cours</MenuItem>
            </Select>
          </FormControl>
        </Stack>


        <Grid container spacing={2} alignItems="stretch">
          <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard icon={<ConfirmationNumberIcon />} label="Tickets ouverts" value={data.tickets?.open || 0} helper={`${data.tickets?.open || 0} ticket(s) ouverts`} />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
            <Card variant="elevation" sx={{ height: '100%', minHeight: 132, border: 0, borderRadius: 3, backgroundImage: 'linear-gradient(145deg, rgba(36,118,185,.12), rgba(255,255,255,.98) 75%)', boxShadow: '0 6px 20px rgba(20,40,60,.08)' }}>
              <CardContent sx={{ p: 2.5, minHeight: 174, '&:last-child': { pb: 2.5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Services actifs</Typography>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center"><Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: 'rgba(0,120,212,.12)', color: '#0078d4', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><MicrosoftIcon fontSize="small" /></Box><Box sx={{ flex: 1 }}><Typography variant="body2" sx={{ fontWeight: 700 }}>Microsoft 365</Typography><Typography variant="caption" color="text.secondary">{data.services?.o365?.users || 0} compte(s), {data.services?.o365?.licenses || 0} licence(s)</Typography></Box><Chip size="small" label={`${data.services?.o365?.used || 0}/${data.services?.o365?.total || 0}`} /></Stack>
                  <Stack direction="row" spacing={1} alignItems="center"><Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: 'rgba(46,125,50,.12)', color: '#2e7d32', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><SecurityIcon fontSize="small" /></Box><Box sx={{ flex: 1 }}><Typography variant="body2" sx={{ fontWeight: 700 }}>ESET</Typography><Typography variant="caption" color="text.secondary">{data.services?.eset?.devices || 0} appareil(s) protégés</Typography></Box><Chip size="small" label={`${data.services?.eset?.used || 0}/${data.services?.eset?.total || 0}`} /></Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard icon={<ComputerIcon />} label="Parc supervisé" value={Math.max(deviceTotal || 0, supervisedDevices)} helper="appareils supervisés" color="success.main" />
          </Grid>
        </Grid>

        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Consommation de votre contrat d'infogérance</Typography>
              <Typography variant="caption" color="text.secondary">Période analysée : {usageRangeLabel}</Typography>
              {usagePeriod === 'month' && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Période contractuelle : {contractPeriodLabel}</Typography>}
            </Box>
          </Stack>
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
              <UsageCard title="Heures admin" used={usageData.admin} total={plannedAdmin} footer="Calculé par la matrice du contrat" />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
              <UsageCard title="Heures ingé" used={usageData.engineer} total={plannedEngineer} footer="Calculé par la matrice du contrat" />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
              <UsageCard title="Heures pilotage" used={usageData.pilotage} total={plannedPilotage} footer="Calculé par la matrice du contrat" />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
              <UsageCard title="Déplacements" used={usageData.travel} total={plannedTravel} unit="count" footer="Déplacements rattachés à l'infogérance" />
            </Grid>
          </Grid>
        </Stack>

        <Grid container spacing={2} alignItems="stretch">
          <Grid item size={{ xs: 12, md: 3 }}>
            <SectionCard title="Vos référents">
              <Stack spacing={1.75}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ color: 'success.main', display: 'inline-flex' }}><AlternateEmailIcon /></Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">Support général</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>{data.support?.email || 'support@toolbox.re'}</Typography>
                  </Box>
                </Stack>
                <Divider />
                <Stack spacing={1.25}>
                  <PortfolioContact label="Référent principal" user={primaryContact} />
                  <PortfolioContact label="Binôme" user={secondaryContact} />
                  {additionalContacts.length ? (
                    <Stack spacing={1.25} sx={{ pt: 0.5 }}>
                      <Divider />
                      <Typography variant="caption" color="text.secondary">Référents</Typography>
                      {additionalContacts.map((contact, index) => (
                        <PortfolioContact key={contact._id || contact.email || contact.name} label={`${index + 3}.`} user={contact} />
                      ))}
                    </Stack>
                  ) : null}
                </Stack>
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item size={{ xs: 12, md: 9 }}>
            <SectionCard
              title="Demandes récentes"
              action={<Button component="a" href="/app/contracts" size="small" startIcon={<AccessTimeIcon />}>Voir les temps</Button>}
            >
              {recentTicketRows.length ? recentTicketRows.map(({ ticket, entries }) => {
                const totalHours = entries.reduce((sum, entry) => sum + Number(entry.hours || 0), 0);
                return (
                  <Accordion
                    key={ticket._id || ticket.ticketId}
                    disableGutters
                    elevation={0}
                    sx={{
                      '&:before': { display: 'none' },
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 58 }}>
                      <Stack direction="row" spacing={1.5} sx={{ width: '100%', pr: 1, alignItems: 'center' }}>
                        <Chip size="small" label={`#${ticket.ticketId}`} variant="outlined" />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{ticket.subject || 'Sans sujet'}</Typography>
                          <Typography variant="caption" color="text.secondary">{ticket.assignedTo || 'Non assigné'} · {formatDate(ticket.updatedAt)}</Typography>
                        </Box>
                        {entries.length ? <Chip size="small" color="success" variant="outlined" label={`${formatHours(totalHours)} · ${entries.length} temps`} /> : null}
                        <Chip size="small" label={ticket.status || 'Statut'} />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 0.5, pb: 1.5 }}>
                      {entries.length ? (
                        <Stack spacing={0.75}>
                          {entries.map((entry) => (
                            <Stack key={entry._id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ pl: 5.5 }}>
                              <Chip size="small" color="success" variant="outlined" label={formatHours(entry.hours)} />
                              <Typography variant="body2" sx={{ flex: 1 }}>{entry.activity || 'Intervention'}</Typography>
                              <Typography variant="caption" color="text.secondary">{entry.user || 'Technicien'} · {formatDate(entry.spentOn)}</Typography>
                              {entry.comments ? <Typography variant="caption" color="text.secondary" sx={{ maxWidth: { sm: 360 } }}>{entry.comments}</Typography> : null}
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 5.5 }}>Aucun temps passé rattaché à cette demande.</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              }) : <Typography variant="body2" color="text.secondary">Aucune demande récente.</Typography>}
            </SectionCard>
          </Grid>

          <Grid item size={{ xs: 12, lg: 12 }}>
            <SectionCard
              title="Journaux récents"
              action={<Button component="a" href="/app/contracts" size="small" startIcon={<HistoryIcon />}>Voir le journal</Button>}
            >
              {(data.journals?.recent || []).length ? <DataGrid
                autoHeight
                rows={data.journals.recent}
                getRowId={(row) => row._id || row.id || `${row.date}-${row.change}`}
                columns={[
                  { field: 'author', headerName: 'Utilisateur', flex: 1, minWidth: 80, renderCell: ({ row }) => { const name = row.authorName || 'Auteur non renseigné'; return <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}><Stack direction="row" spacing={1} sx={{alignItems:"center"}}><Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: 'success.main' }}>{getInitials(name)}</Avatar><Typography variant="body2" sx={{ fontWeight: 700 }}>{name}</Typography></Stack></Box>; } },
                  { field: 'date', headerName: 'Date', width: 125, align: 'center', headerAlign: 'center', renderCell: ({ row }) => <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Chip size="small" label={formatDate(row.date)} variant="outlined" /></Box> },
                  { field: 'type', headerName: 'Type', width: 145, align: 'center', headerAlign: 'center', renderCell: ({ row }) => <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Chip size="small" label={TYPES.find((item) => item.value === row.type)?.label || 'Technique'} color={row.type === 'commercial' ? 'warning' : row.type === 'administratif' ? 'info' : 'success'} /></Box> },
                  { field: 'change', headerName: 'Changement', flex: 1.8, minWidth: 280, renderCell: ({ row }) => <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}><Typography variant="body2" sx={{ fontWeight: 700 }}>{row.change || 'Activité client'}</Typography>{row.ticketId ? <Typography variant="caption" color="text.secondary">Ticket #{row.ticketId}</Typography> : null}</Box> },
                ]}
                pageSizeOptions={[5, 10, 25]}
                initialState={{ pagination: { paginationModel: { pageSize: 5, page: 0 } } }}
                rowHeight={64}
                columnHeaderHeight={52}
                disableRowSelectionOnClick
                sx={{ border: 0, '& .MuiDataGrid-columnHeaders': { bgcolor: 'red', color: '#253858', fontWeight: 800, borderBottom: '1px solid #e2e6ee' }, '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 800 }, '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', py: 1.25 }, '& .MuiDataGrid-cellContent': { width: '100%' } }}
              /> : <Typography variant="body2" color="text.secondary">Aucun journal récent.</Typography>}
            </SectionCard>
          </Grid>
        </Grid>

      </Stack>
    </Box>
  );
}
